import { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Send, Loader2, AlertTriangle, Pencil } from 'lucide-react';
import { moderateContent } from '@/types/courage';
import { Button } from '@/components/ui/button';
import { WizardProgress } from './WizardProgress';
import { PhotoUploadStep } from './steps/PhotoUploadStep';
import { CustomDesignStep } from './steps/CustomDesignStep';
import { CombinedEntryStep } from './steps/CombinedEntryStep';
import { CourageModeStep } from './steps/CourageModeStep';
import { IdentityChoiceStep } from './steps/IdentityChoiceStep';
import { MessageStep } from './steps/MessageStep';
import { getAutoAnimationTheme } from '@/lib/autoAssign';
import { PreviewStep } from './steps/PreviewStep';
import { ScheduleStep, type ScheduleData, type ShareMethod, INITIAL_SCHEDULE_DATA } from './steps/ScheduleStep';
import { ShareDialog } from '@/components/sharing/ShareDialog';
import { ConfessionShare } from '@/components/sharing/ConfessionShare';
import { UpsellModal, useUpsellModal } from '@/components/credits/UpsellModals';
import { PostPublishReinforcementModal } from '@/components/dashboard/PostPublishReinforcementModal';
import { useAnnouncementWizard } from '@/hooks/useAnnouncementWizard';
import { usePostPublishSuccess } from '@/hooks/usePostPublishSuccess';
import { useJacKeyNudgeContext } from '@/contexts/JacKeyNudgeContext';
import { useEventTracking } from '@/hooks/useEventTracking';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { getAnnouncementShareUrl } from '@/lib/shareUrl';
import { buildShareLinks } from '@/lib/buildShareLinks';
import { getShareText } from '@/lib/shareText';
import { openSms, openExternal, copyWithToast } from '@/lib/shareLaunch';
import type { CourageModeData } from '@/types/courage';
import { INITIAL_COURAGE_DATA } from '@/types/courage';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';


const wizardCopy = {
  en: { goBack: 'Go back', saveForNow: 'Save for now', keepGoing: 'Keep going', updateSchedule: 'Update & Schedule', scheduleAnnouncement: 'Schedule Announcement', updatePublish: 'Update & Publish', publishAnnouncement: 'Publish Announcement', publishSendEmail: 'Publish & Send Email', privacyNote: 'Nothing is shared automatically. You control who sees this.', linkCopied: 'Link copied to clipboard!', linkCopyFail: 'Failed to copy link', emailSent: 'Email sent to your recipient!', emailFail: 'Published but email delivery failed. You can copy the link from the dashboard.', emailFailLink: 'Message published but email delivery failed. You can still share the link.', dailyLimit: "You've reached your daily limit of 5 anonymous messages. Your limit resets in 24 hours.", remainingMsg: 'You have {n} anonymous message{s} remaining today.', somethingWrong: 'Something went wrong. Please try again.', draftFail: 'Failed to save draft. Please try again.' },
  es: { goBack: 'Volver', saveForNow: 'Guardar por ahora', keepGoing: 'Continuar', updateSchedule: 'Actualizar y Programar', scheduleAnnouncement: 'Programar Anuncio', updatePublish: 'Actualizar y Publicar', publishAnnouncement: 'Publicar Anuncio', publishSendEmail: 'Publicar y Enviar Email', privacyNote: 'Nada se comparte automáticamente. Tú controlas quién ve esto.', linkCopied: '¡Enlace copiado!', linkCopyFail: 'Error al copiar enlace', emailSent: '¡Email enviado a tu destinatario!', emailFail: 'Publicado pero el envío de email falló. Puedes copiar el enlace desde el panel.', emailFailLink: 'Mensaje publicado pero el email falló. Aún puedes compartir el enlace.', dailyLimit: 'Has alcanzado el límite diario de 5 mensajes anónimos. Se restablece en 24 horas.', remainingMsg: 'Te quedan {n} mensaje{s} anónimo{s} hoy.', somethingWrong: 'Algo salió mal. Inténtalo de nuevo.', draftFail: 'Error al guardar borrador. Inténtalo de nuevo.' },
  fr: { goBack: 'Retour', saveForNow: 'Enregistrer pour l\'instant', keepGoing: 'Continuer', updateSchedule: 'Mettre à jour et Programmer', scheduleAnnouncement: 'Programmer l\'Annonce', updatePublish: 'Mettre à jour et Publier', publishAnnouncement: 'Publier l\'Annonce', publishSendEmail: 'Publier et Envoyer par Email', privacyNote: 'Rien n\'est partagé automatiquement. Vous contrôlez qui voit ceci.', linkCopied: 'Lien copié !', linkCopyFail: 'Échec de la copie du lien', emailSent: 'Email envoyé à votre destinataire !', emailFail: 'Publié mais l\'envoi d\'email a échoué. Vous pouvez copier le lien depuis le tableau de bord.', emailFailLink: 'Message publié mais l\'email a échoué. Vous pouvez toujours partager le lien.', dailyLimit: 'Vous avez atteint votre limite quotidienne de 5 messages anonymes. La limite se réinitialise dans 24 heures.', remainingMsg: 'Il vous reste {n} message{s} anonyme{s} aujourd\'hui.', somethingWrong: 'Un problème est survenu. Réessayez.', draftFail: 'Échec de la sauvegarde du brouillon. Réessayez.' },
  pt: { goBack: 'Voltar', saveForNow: 'Salvar por enquanto', keepGoing: 'Continuar', updateSchedule: 'Atualizar e Agendar', scheduleAnnouncement: 'Agendar Anúncio', updatePublish: 'Atualizar e Publicar', publishAnnouncement: 'Publicar Anúncio', publishSendEmail: 'Publicar e Enviar Email', privacyNote: 'Nada é compartilhado automaticamente. Você controla quem vê isso.', linkCopied: 'Link copiado!', linkCopyFail: 'Falha ao copiar link', emailSent: 'Email enviado ao seu destinatário!', emailFail: 'Publicado mas o envio de email falhou. Você pode copiar o link do painel.', emailFailLink: 'Mensagem publicada mas o email falhou. Você ainda pode compartilhar o link.', dailyLimit: 'Você atingiu o limite diário de 5 mensagens anônimas. O limite se renova em 24 horas.', remainingMsg: 'Você tem {n} mensagem(ns) anônima(s) restante(s) hoje.', somethingWrong: 'Algo deu errado. Tente novamente.', draftFail: 'Falha ao salvar rascunho. Tente novamente.' },
  ar: { goBack: 'رجوع', saveForNow: 'حفظ الآن', keepGoing: 'متابعة', updateSchedule: 'تحديث وجدولة', scheduleAnnouncement: 'جدولة الإعلان', updatePublish: 'تحديث ونشر', publishAnnouncement: 'نشر الإعلان', publishSendEmail: 'نشر وإرسال بريد', privacyNote: 'لا شيء يُشارك تلقائياً. أنت تتحكم في من يرى هذا.', linkCopied: 'تم نسخ الرابط!', linkCopyFail: 'فشل نسخ الرابط', emailSent: 'تم إرسال البريد إلى المستلم!', emailFail: 'تم النشر لكن فشل إرسال البريد. يمكنك نسخ الرابط من لوحة التحكم.', emailFailLink: 'تم نشر الرسالة لكن فشل البريد. لا يزال بإمكانك مشاركة الرابط.', dailyLimit: 'لقد وصلت إلى الحد اليومي من 5 رسائل مجهولة. يتم التجديد خلال 24 ساعة.', remainingMsg: 'لديك {n} رسالة مجهولة متبقية اليوم.', somethingWrong: 'حدث خطأ. حاول مرة أخرى.', draftFail: 'فشل حفظ المسودة. حاول مرة أخرى.' },
  ja: { goBack: '戻る', saveForNow: '一時保存', keepGoing: '次へ', updateSchedule: '更新してスケジュール', scheduleAnnouncement: 'アナウンスを予約', updatePublish: '更新して公開', publishAnnouncement: 'アナウンスを公開', publishSendEmail: '公開してメール送信', privacyNote: '自動的に共有されることはありません。誰が見るかはあなたが決めます。', linkCopied: 'リンクをコピーしました！', linkCopyFail: 'リンクのコピーに失敗しました', emailSent: 'メールを送信しました！', emailFail: '公開されましたがメール送信に失敗しました。ダッシュボードからリンクをコピーできます。', emailFailLink: 'メッセージは公開されましたがメール送信に失敗しました。リンクを共有できます。', dailyLimit: '匿名メッセージの1日の上限（5通）に達しました。24時間後にリセットされます。', remainingMsg: '本日残り{n}通の匿名メッセージが送信可能です。', somethingWrong: 'エラーが発生しました。もう一度お試しください。', draftFail: '下書きの保存に失敗しました。もう一度お試しください。' },
  ko: { goBack: '뒤로', saveForNow: '임시 저장', keepGoing: '계속하기', updateSchedule: '수정 및 예약', scheduleAnnouncement: '공지 예약', updatePublish: '수정 및 게시', publishAnnouncement: '공지 게시', publishSendEmail: '게시 및 이메일 발송', privacyNote: '자동으로 공유되지 않습니다. 누가 볼지는 당신이 결정합니다.', linkCopied: '링크가 복사되었습니다!', linkCopyFail: '링크 복사 실패', emailSent: '이메일이 전송되었습니다!', emailFail: '게시되었지만 이메일 전송에 실패했습니다. 대시보드에서 링크를 복사할 수 있습니다.', emailFailLink: '메시지가 게시되었지만 이메일 전송에 실패했습니다. 링크를 공유할 수 있습니다.', dailyLimit: '익명 메시지 일일 한도(5통)에 도달했습니다. 24시간 후 리셋됩니다.', remainingMsg: '오늘 {n}개의 익명 메시지를 더 보낼 수 있습니다.', somethingWrong: '문제가 발생했습니다. 다시 시도해주세요.', draftFail: '초안 저장에 실패했습니다. 다시 시도해주세요.' },
  id: { goBack: 'Kembali', saveForNow: 'Simpan dulu', keepGoing: 'Lanjutkan', updateSchedule: 'Perbarui & Jadwalkan', scheduleAnnouncement: 'Jadwalkan Pengumuman', updatePublish: 'Perbarui & Terbitkan', publishAnnouncement: 'Terbitkan Pengumuman', publishSendEmail: 'Terbitkan & Kirim Email', privacyNote: 'Tidak ada yang dibagikan secara otomatis. Anda menentukan siapa yang melihat ini.', linkCopied: 'Tautan disalin!', linkCopyFail: 'Gagal menyalin tautan', emailSent: 'Email terkirim ke penerima!', emailFail: 'Diterbitkan tapi pengiriman email gagal. Anda bisa menyalin tautan dari dasbor.', emailFailLink: 'Pesan diterbitkan tapi email gagal. Anda masih bisa membagikan tautan.', dailyLimit: 'Anda telah mencapai batas harian 5 pesan anonim. Batas diperbarui dalam 24 jam.', remainingMsg: 'Anda memiliki {n} pesan anonim tersisa hari ini.', somethingWrong: 'Terjadi kesalahan. Coba lagi.', draftFail: 'Gagal menyimpan draf. Coba lagi.' },
  hi: { goBack: 'वापस जाएं', saveForNow: 'अभी सहेजें', keepGoing: 'आगे बढ़ें', updateSchedule: 'अपडेट और शेड्यूल करें', scheduleAnnouncement: 'घोषणा शेड्यूल करें', updatePublish: 'अपडेट और प्रकाशित करें', publishAnnouncement: 'घोषणा प्रकाशित करें', publishSendEmail: 'प्रकाशित करें और ईमेल भेजें', privacyNote: 'कुछ भी स्वचालित रूप से साझा नहीं होता। आप नियंत्रित करते हैं कि कौन देखे।', linkCopied: 'लिंक कॉपी हो गया!', linkCopyFail: 'लिंक कॉपी विफल', emailSent: 'ईमेल भेजा गया!', emailFail: 'प्रकाशित लेकिन ईमेल विफल। डैशबोर्ड से लिंक कॉपी करें।', emailFailLink: 'संदेश प्रकाशित लेकिन ईमेल विफल। लिंक साझा करें।', dailyLimit: 'आपने 5 अनाम संदेशों की दैनिक सीमा पूरी कर ली। 24 घंटे में रीसेट होगा।', remainingMsg: 'आज {n} अनाम संदेश शेष हैं।', somethingWrong: 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।', draftFail: 'ड्राफ्ट सहेजने में विफल। पुनः प्रयास करें।' },
};

interface AnnouncementWizardProps {
  announcementId?: string;
}

export function AnnouncementWizard({ announcementId }: AnnouncementWizardProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const wc = lc(wizardCopy, language);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [confessionShareOpen, setConfessionShareOpen] = useState(false);
  const [reinforcementOpen, setReinforcementOpen] = useState(false);
  const [publishedAnnouncement, setPublishedAnnouncement] = useState<{ public_slug: string; recipient_name: string; id?: string; template?: string; relationship?: string; message?: string; photo_urls?: string[] | null; published_at?: string | null; created_at?: string } | null>(null);
  const [scheduleData, setScheduleData] = useState<ScheduleData>(INITIAL_SCHEDULE_DATA);
  const [courageData, setCourageData] = useState<CourageModeData>(INITIAL_COURAGE_DATA);
  const [selectedShareMethod, setSelectedShareMethod] = useState<ShareMethod>(null);
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');

  const updateCourageData = (updates: Partial<CourageModeData>) => {
    setCourageData(prev => ({ ...prev, ...updates }));
  };
  
  const {
    modalState,
    showNoCreditsModal,
    showFirstAnnouncementModal,
    setModalOpen,
  } = useUpsellModal();

  const { triggerSuccess } = usePostPublishSuccess();
  const { showBadgeNudge } = useJacKeyNudgeContext();
  const { trackEvent } = useEventTracking();
  const {
    currentStep,
    totalSteps,
    formData,
    isSubmitting,
    isLoading,
    loadError,
    isEditMode,
    existingPhotoUrls,
    updateFormData,
    nextStep,
    prevStep,
    goToStep,
    saveAsDraft,
    publishAnnouncement,
    canProceed,
    removeExistingPhoto,
  } = useAnnouncementWizard({ announcementId });

  // Scroll to top of page when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const updateScheduleData = (updates: Partial<ScheduleData>) => {
    setScheduleData(prev => ({ ...prev, ...updates }));
  };

  // Execute the selected sharing action after publish — no pre-opened windows needed
  const executeShareAction = async (slug: string, announcementResult: any) => {
    const shareUrl = getAnnouncementShareUrl(slug);
    const senderName = user?.user_metadata?.full_name || user?.user_metadata?.name || null;
    const socialLinks = buildShareLinks({
      publicSlug: slug,
      templateSlug: announcementResult.template,
      relationship: announcementResult.relationship,
      senderName,
    });

    switch (selectedShareMethod) {
      case 'copy_link': {
        await copyWithToast(shareUrl, wc.linkCopied, wc.linkCopyFail);
        break;
      }
      case 'email': {
        if (shareRecipientEmail.trim()) {
          try {
            const { data: emailData, error } = await supabase.functions.invoke('send-anonymous-email', {
              body: {
                announcementId: announcementResult.id,
                recipientEmail: shareRecipientEmail.trim(),
                recipientName: announcementResult.recipient_name || '',
              },
            });
            if (error) {
              console.error('Email invoke error:', error);
              throw error;
            }
            if (emailData?.error) {
              console.error('Email function error:', emailData.error);
              throw new Error(emailData.error);
            }
            toast.success(wc.emailSent);
          } catch (e) {
            console.error('Email delivery failed:', e);
            toast.error(wc.emailFail);
          }
        }
        break;
      }
      case 'sms': {
        const personalizedText = getShareText(senderName, announcementResult.relationship);
        openSms(`${personalizedText} ${shareUrl}`);
        break;
      }
      case 'facebook': {
        openExternal(socialLinks.facebook);
        break;
      }
      case 'x': {
        openExternal(socialLinks.x);
        break;
      }
      case 'whatsapp': {
        openExternal(socialLinks.whatsapp);
        break;
      }
    }
  };

  const handlePublish = async () => {
    const isCrush = formData.relationship === 'crush';
    
    if (isCrush) {
      trackEvent('crush_publish_attempted', {
        identity_mode: courageData.courageMode,
        delivery_mode: courageData.deliveryMode,
      });
    }

    try {
      const result = await publishAnnouncement(isCrush ? courageData : undefined);

      if (!result) {
        return;
      }

      if ('needsCredits' in result && result.needsCredits) {
        showNoCreditsModal();
        return;
      }

      if ('public_slug' in result && result.public_slug) {
        if (isCrush) {
          trackEvent('crush_published', {
            identity_mode: courageData.courageMode,
            message_length: formData.message.length,
          });

          if (courageData.deliveryMode === 'email_relay' && courageData.recipientEmail) {
            try {
              const { data: emailData, error: emailError } = await supabase.functions.invoke('send-anonymous-email', {
                body: {
                  announcementId: result.id,
                  recipientEmail: courageData.recipientEmail,
                  recipientName: formData.recipientName,
                },
              });
              if (emailError) {
                console.error('Email relay error:', emailError);
                toast.error(wc.emailFailLink);
              } else {
                toast.success(wc.emailSent);
                if (emailData && typeof emailData.remaining === 'number' && emailData.remaining <= 3) {
                  setTimeout(() => {
                    if (emailData.remaining === 0) {
                      toast.warning(wc.dailyLimit);
                    } else {
                      toast.warning(wc.remainingMsg.replace('{n}', String(emailData.remaining)).replace('{s}', emailData.remaining === 1 ? '' : 's'));
                    }
                  }, 1500);
                }
              }
            } catch (e) {
              console.error('Email relay catch error:', e);
              toast.error(wc.emailFailLink);
            }
          }
        }

        const announcementData = {
          public_slug: result.public_slug,
          recipient_name: result.recipient_name,
          id: result.id,
          template: result.template,
          relationship: result.relationship,
          message: result.message,
          photo_urls: result.photo_urls,
          published_at: result.published_at,
          created_at: result.created_at,
        };

        setPublishedAnnouncement(announcementData);
        
        // For SMS: we MUST let the protocol handler fire and the OS take over
        // BEFORE any React state updates or DOM mutations, otherwise the browser
        // cancels the sms: navigation. So we await the full handoff first.
        if (selectedShareMethod === 'sms') {
          // openSms returns a promise that resolves after the OS has had 1.5s
          // to intercept. No React state changes happen during this window.
          await executeShareAction(result.public_slug, announcementData);

          // Now that the SMS app has (or hasn't) opened, wait for the user to
          // come back before showing any modal. Listen for visibility change
          // with a 30-second max wait so we don't hang forever.
          await new Promise<void>((resolve) => {
            // If the page is already hidden (SMS app opened), wait for return
            if (document.hidden) {
              const onReturn = () => {
                if (!document.hidden) {
                  document.removeEventListener('visibilitychange', onReturn);
                  resolve();
                }
              };
              document.addEventListener('visibilitychange', onReturn);
              // Safety timeout — don't block forever
              setTimeout(() => {
                document.removeEventListener('visibilitychange', onReturn);
                resolve();
              }, 30000);
            } else {
              // Page never left — fallback already handled by openSms, proceed
              resolve();
            }
          });
        } else {
          await executeShareAction(result.public_slug, announcementData);
        }
        
        if ('earnedBadges' in result && Array.isArray(result.earnedBadges)) {
          result.earnedBadges.forEach((badge: { badgeKey?: string }) => {
            if (badge.badgeKey) {
              showBadgeNudge(badge.badgeKey);
            }
          });
        }
        
        // Persist post-publish event so dashboard can scroll + animate puzzle/fruit update
        triggerSuccess('publish', result.public_slug, result.recipient_name);

        // Show reinforcement modal AFTER share action has been executed
        setReinforcementOpen(true);
      } else if (result) {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('[Publish] Unexpected error:', err);
      toast.error(wc.somethingWrong);
    }
  };

  const [userHasShared, setUserHasShared] = useState(false);

  const handleShareDialogClose = (open: boolean) => {
    setShareDialogOpen(open);
    if (!open) {
      if (userHasShared) {
        sessionStorage.setItem('sayitnow_post_share_gift_prompt', '1');
      }
      window.location.href = '/dashboard#puzzle';
    }
  };

  const handleSaveDraft = async () => {
    try {
      const result = await saveAsDraft();
      if (result) {
        if ('earnedBadges' in result && Array.isArray(result.earnedBadges)) {
          result.earnedBadges.forEach((badge: { badgeKey?: string }) => {
            if (badge.badgeKey) {
              showBadgeNudge(badge.badgeKey);
            }
          });
        }
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('[SaveDraft] Unexpected error:', err);
      toast.error(wc.draftFail);
    }
  };

  const isCrush = formData.relationship === 'crush';
  const actualTotalSteps = 6;

  // Check if publish is allowed on step 6 (share method must be selected)
  const isShareMethodReady = (): boolean => {
    if (!selectedShareMethod) return false;
    if (selectedShareMethod === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareRecipientEmail.trim());
    }
    return true;
  };

  const getStepComponent = (step: number) => {
    // Auto-assign animation theme based on template whenever we render
    const autoTheme = getAutoAnimationTheme(formData.template);
    if (formData.animationTheme !== autoTheme) {
      updateFormData({ animationTheme: autoTheme });
    }

    if (isCrush) {
      switch (step) {
        case 1: return <CombinedEntryStep formData={formData} updateFormData={updateFormData} />;
        case 2: return <MessageStep formData={formData} updateFormData={updateFormData} />;
        case 3: return <CourageModeStep courageData={courageData} updateCourageData={updateCourageData} recipientName={formData.recipientName} saveAsDraft={saveAsDraft} />;
        case 4: return formData.template === 'custom'
          ? <CustomDesignStep formData={formData} updateFormData={updateFormData} existingPhotoUrls={existingPhotoUrls} onRemoveExistingPhoto={removeExistingPhoto} />
          : <PhotoUploadStep formData={formData} updateFormData={updateFormData} existingPhotoUrls={existingPhotoUrls} onRemoveExistingPhoto={removeExistingPhoto} />;
        case 5: return <PreviewStep formData={formData} existingPhotoUrls={existingPhotoUrls} updateFormData={updateFormData} />;
        case 6: return <ScheduleStep scheduleData={scheduleData} updateScheduleData={updateScheduleData} publishedAnnouncement={publishedAnnouncement} announcementId={announcementId} selectedShareMethod={selectedShareMethod} onShareMethodChange={setSelectedShareMethod} recipientEmail={shareRecipientEmail} onRecipientEmailChange={setShareRecipientEmail} />;
        default: return null;
      }
    }
    switch (step) {
      case 1: return <CombinedEntryStep formData={formData} updateFormData={updateFormData} />;
      case 2: return <MessageStep formData={formData} updateFormData={updateFormData} />;
      case 3: return <IdentityChoiceStep formData={formData} updateFormData={updateFormData} />;
      case 4: return formData.template === 'custom'
        ? <CustomDesignStep formData={formData} updateFormData={updateFormData} existingPhotoUrls={existingPhotoUrls} onRemoveExistingPhoto={removeExistingPhoto} />
        : <PhotoUploadStep formData={formData} updateFormData={updateFormData} existingPhotoUrls={existingPhotoUrls} onRemoveExistingPhoto={removeExistingPhoto} />;
      case 5: return <PreviewStep formData={formData} existingPhotoUrls={existingPhotoUrls} updateFormData={updateFormData} />;
      case 6: return <ScheduleStep scheduleData={scheduleData} updateScheduleData={updateScheduleData} publishedAnnouncement={publishedAnnouncement} announcementId={announcementId} selectedShareMethod={selectedShareMethod} onShareMethodChange={setSelectedShareMethod} recipientEmail={shareRecipientEmail} onRecipientEmailChange={setShareRecipientEmail} />;
      default: return null;
    }
  };

  const renderStep = () => getStepComponent(currentStep);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center px-4">
        <div className="max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-soft">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{loadError}</p>
          <Button className="mt-5" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Share Dialog (kept for potential edge cases) */}
      {publishedAnnouncement && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={handleShareDialogClose}
          publicSlug={publishedAnnouncement.public_slug}
          recipientName={publishedAnnouncement.recipient_name}
          onShared={() => setUserHasShared(true)}
          announcementId={publishedAnnouncement.id}
          template={publishedAnnouncement.template}
          relationship={publishedAnnouncement.relationship}
          announcement={publishedAnnouncement}
        />
      )}

      {/* Confession Share */}
      {confessionShareOpen && publishedAnnouncement && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <ConfessionShare publicSlug={publishedAnnouncement.public_slug} />
            <div className="mt-6 text-center">
              <Button variant="ghost" onClick={() => { setConfessionShareOpen(false); window.location.href = '/dashboard#puzzle'; }}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Publish Reinforcement Modal */}
      <PostPublishReinforcementModal
        open={reinforcementOpen}
        onClose={() => {
          setReinforcementOpen(false);
        }}
        onShare={() => {
          setReinforcementOpen(false);
          window.location.href = '/dashboard#puzzle';
        }}
        recipientName={publishedAnnouncement?.recipient_name}
      />

      {/* Upsell Modal */}
      <UpsellModal
        type={modalState.type}
        open={modalState.open}
        onOpenChange={setModalOpen}
        creditsAdded={modalState.creditsAdded}
        recipientName={publishedAnnouncement?.recipient_name}
      />

      {/* Progress bar */}
      <WizardProgress
        currentStep={currentStep}
        totalSteps={actualTotalSteps}
        onStepClick={goToStep}
      />

      {/* Step content */}
      <div className="container max-w-4xl py-8 px-4" ref={stepContainerRef}>
        <AnimatePresence mode="wait">
          <motion.div key={currentStep}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-12 pt-6 border-t border-border">
          <div>
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={prevStep}
                disabled={isSubmitting}
                className="gap-2 hover-soft"
              >
                <ArrowLeft className="h-4 w-4" />
                {wc.goBack}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Save as draft (available after step 1 with relationship set) */}
            {currentStep >= 1 && formData.relationship && (
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSubmitting}
                className="gap-2 hover-soft"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {wc.saveForNow}
              </Button>
            )}

            {/* Next / Publish button */}
            {currentStep < actualTotalSteps ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed(currentStep, courageData) || isSubmitting}
                className="gap-2 hover-soft"
              >
                {wc.keepGoing}
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              /* Only show Publish when a share method is selected */
              isShareMethodReady() && (() => {
                const publishMod = moderateContent(formData.message || '');
                return (
                  <div className="flex flex-col items-end gap-2">
                    {publishMod.blocked && (
                      <div className="w-full max-w-[420px] rounded-lg border border-destructive/60 bg-destructive/10 p-3 text-left">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                          <div className="space-y-1.5 text-xs">
                            <p className="font-medium text-destructive">
                              {publishMod.reason || 'This message may violate our community guidelines.'}
                            </p>
                            {publishMod.matchedSentence && (
                              <p className="text-muted-foreground italic">
                                "{publishMod.matchedSentence.length > 160 ? publishMod.matchedSentence.slice(0, 160) + '…' : publishMod.matchedSentence}"
                              </p>
                            )}
                            {publishMod.suggestion && (
                              <p className="text-muted-foreground">{publishMod.suggestion}</p>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 mt-1 gap-1.5"
                              onClick={() => goToStep(2)}
                            >
                              <Pencil className="h-3 w-3" />
                              Edit message
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    <Button
                      onClick={handlePublish}
                      disabled={isSubmitting || publishMod.blocked}
                      className="gap-2 bg-primary hover-soft"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {isCrush && courageData.deliveryMode === 'email_relay'
                        ? wc.publishSendEmail
                        : scheduleData.isScheduled
                          ? (isEditMode ? wc.updateSchedule : wc.scheduleAnnouncement)
                          : (isEditMode ? wc.updatePublish : wc.publishAnnouncement)
                      }
                    </Button>
                    <p className="text-xs text-muted-foreground max-w-[240px] text-right">
                      {wc.privacyNote}
                    </p>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
